import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Diagnostic } from '@awesome-cordova-plugins/diagnostic/ngx';
import { faKeyboard } from '@fortawesome/pro-regular-svg-icons';
import { faMicrophone as faMicrophoneReg } from '@fortawesome/pro-regular-svg-icons';
import { faMicrophone, faRepeat, faClose, faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons';
import { Platform } from '@ionic/angular';

import { distinctUntilChanged } from 'rxjs';

import { WaveConfigOptions } from '@empathy/audio';

import { AIChatType } from '../../../../models/ai-chat-type.enum';

@Component({
  selector: 'empathy-keyboard-audio',
  templateUrl: 'keyboard-audio.component.html',
  styleUrls: ['./keyboard-audio.component.scss']
})
export class KeyboardAudioComponent implements OnInit {
  @Input() context: string;

  // @Output() answerAudio = new EventEmitter<{ audio: Blob; transcription: string }>();
  @Output() answerText = new EventEmitter<string>();
  @Output() processing = new EventEmitter<boolean>();
  @Output() transcribeError = new EventEmitter<void>();

  processing$ = signal(false);
  stateType$ = signal<AIChatType>(AIChatType.Recorder);
  transcriptionFailed$ = signal(false);
  userTyped$ = signal(false);

  aIChatType = AIChatType;
  answerCtrl = new FormControl('', Validators.required);
  audioSettings: WaveConfigOptions = {
    autoScroll: true,
    autoCenter: true,
    dragToSeek: true,
    fillParent: true,
    barGap: 2,
    barRadius: 10,
    barWidth: 2,
    cursorColor: 'rgba(172, 212, 52, 1)',
    cursorWidth: 2,
    height: 24,
    normalize: false,
    progressColor: 'rgb(23, 92, 211)',
    waveColor: 'rgba(56, 58, 62, 0.7)'
  };

  faClose = faClose;
  faKeyboard = faKeyboard;
  faMicrophone = faMicrophone;
  faMicrophoneReg = faMicrophoneReg;
  faPaperPlaneTop = faPaperPlaneTop;
  faRepeat = faRepeat;

  private diagnostic = inject(Diagnostic);
  private platform = inject(Platform);
  private snackbar = inject(MatSnackBar);

  private lastAudio: { local: Blob; api: Blob } = { local: null, api: null };

  ngOnInit(): void {
    this.answerCtrl.valueChanges.pipe(distinctUntilChanged()).subscribe((answer) => {
      this.userTyped$.set(!!answer);
    });
  }

  onAnswer(): void {
    this.lastAudio = null;
    this.transcriptionFailed$.set(false);

    if (!this.answerCtrl.valid || this.answerCtrl.value.trim().length === 0) {
      return;
    }

    this.answerText.emit(this.answerCtrl.value);
    this.answerCtrl.reset();
  }

  onAudioRecorderError(): void {
    this.showNotification('Failed to get microphone access, please use the keyboard');
    this.stateType$.set(AIChatType.Keyboard);
  }

  async onAnswerAudio(audio: Blob): Promise<void> {
    this.lastAudio = { local: null, api: null };
    this.transcriptionFailed$.set(false);
    this.processing$.set(true);
    this.processing.emit(true);

    const { audioLocal, audioAPI } = await this.parseAudioResponse(audio);
    this.requestTranscription(audioLocal, audioAPI);
  }

  onResendAudio(): void {
    this.transcriptionFailed$.set(false);
    this.processing$.set(true);
    this.processing.emit(true);
    this.requestTranscription(this.lastAudio.local, this.lastAudio.api);
  }

  onDismiss(): void {
    this.lastAudio = { local: null, api: null };
    this.transcriptionFailed$.set(false);
    this.stateType$.set(AIChatType.Recorder);
  }

  onEnableStartRecordingLayout(): void {
    if (!this.platform.is('mobile') || this.platform.is('mobileweb')) {
      this.stateType$.set(AIChatType.Recording);
    } else {
      this.diagnostic
        .isMicrophoneAuthorized()
        .then((authorized) => {
          if (authorized) {
            this.stateType$.set(AIChatType.Recording);
          } else {
            this.diagnostic
              .requestMicrophoneAuthorization()
              .then((status) => {
                if (status === this.diagnostic.permissionStatus.GRANTED) {
                  this.stateType$.set(AIChatType.Recording);
                } else {
                  this.showNotification('The application has no permissions to access the microphone');
                }
              })
              .catch(() => {
                this.showNotification('Failed to get microphone access, please use the keyboard');
                this.stateType$.set(AIChatType.Keyboard);
              });
          }
        })
        .catch(() => {
          this.showNotification('Failed to get microphone access, please use the keyboard');
          this.stateType$.set(AIChatType.Keyboard);
        });
    }
  }

  private requestTranscription(audio: Blob, audioAPI: Blob): void {
    // Legacy audio transcription is disabled because AudioChatService no longer exposes
    // getAudioTranscriptionReference. Keep the deprecated viewer buildable until it is removed.
    this.processing$.set(false);
    this.processing.emit(false);
    this.transcriptionFailed$.set(true);
    this.lastAudio.local = audio;
    this.lastAudio.api = audioAPI;
  }

  private async parseAudioResponse(audio: Blob): Promise<{ audioLocal: Blob; audioAPI: Blob }> {
    if (this.platform.is('ios')) {
      const data = await audio.arrayBuffer();

      const parsedAudio = new Blob([data], { type: 'audio/mp3' });
      const parsedAPIAudio = new Blob([data], { type: 'audio/webm;codecs=opus' });
      return { audioLocal: parsedAudio, audioAPI: parsedAPIAudio };
    } else {
      return { audioLocal: audio, audioAPI: audio };
    }
  }

  private showNotification(notification: string): void {
    this.snackbar.open(notification, 'Ok', { duration: 3000, panelClass: 'error' });
  }
}
